import React, { useEffect, useReducer, useState } from 'react';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { getBuildingPath } from 'shared/routes/building';
import { getBuildingToolsPath } from 'shared/routes/networking';
import CardActionButtons from 'features/networking/ui/CardActionButtons';
import { TabbedCard } from 'shared/components/abstract/interactables';
import { getBuilding } from 'shared/api/buildings';
import { useRequest } from 'shared/hooks';
import { dataReducer, DataReducerActions, initialDataReducerState } from 'shared/reducers';
import { INTERNET_TYPES } from 'shared/constants/building';
import BasicInfo from 'features/networking/building/BasicInfo';
import ZoneInfo from 'features/networking/building/ZoneInfo';
import VlansPanel from 'features/networking/building/VlansPanel';
import VTRPanel from 'features/networking/building/VTRPanel';

/**
 * @param {Object} props
 * @param {string} props.id - Building ID
 */
function BuildingCard({ id }) {
  const { search } = useLocation();
  const history = useHistory();
  const { request } = useRequest();

  const [state, dispatch] = useReducer(dataReducer, initialDataReducerState);
  const [updateCount, setUpdateCount] = useState(0);

  const triggerUpdate = () => setUpdateCount(updateCount + 1);
  const {
    data,
    dirty,
  } = state;

  if (state.data) state.data.vlanDistributionType = state.data?.dot1x?.vlan_distribution_type;

  const {
    id: buildingId,
    dot1x,
    service_type: serviceType,
  } = data || {};

  useEffect(() => {
    if (id && id > 0) {
      (async () => {
        const result = await request(getBuilding(id, '?expand=units&expand=nas'));
        if (result) {
          dispatch({
            action: DataReducerActions.set,
            payload: result,
          });
        }
      })();
    }
  }, [id, request, updateCount]);

  if (data === undefined) return null;

  const tabs = [
    {
      label: 'General',
      id: buildingId,
      content: <BasicInfo state={state} dispatch={dispatch} />,
    },
    {
      label: 'Nas/VTR',
      id: buildingId,
      disabled: !(Object.values(INTERNET_TYPES)
        .includes(serviceType)
        && serviceType !== INTERNET_TYPES.none),
      content: <VTRPanel
        building={data}
        triggerUpdate={triggerUpdate}
        updateCount={updateCount}
      />,
    },
    {
      label: 'Vlans',
      id: buildingId,
      disabled: !dot1x || !dot1x.nas_profile_id,
      content: <VlansPanel
        state={state}
        dispatch={dispatch}
        triggerUpdate={triggerUpdate}
        updateCount={updateCount}
      />,
    },
    {
      label: 'Zone',
      id: buildingId,
      disabled: !dot1x || !dot1x.nas_profile_id,
      content: <ZoneInfo
        buildingId={buildingId}
        buildingUnits={data?.units}
        updateCount={updateCount}
        triggerUpdate={triggerUpdate}
      />,
    },
  ];

  const close = () => {
    if (dirty) {
      dispatch({ action: DataReducerActions.clear });
    } else {
      const searchParams = new URLSearchParams(search);
      history.push(getBuildingToolsPath(searchParams
        .getAll('building_id')
        .filter((bId) => bId !== id.toString())));
    }
  };

  return (
    <TabbedCard
      key={`building-card-${buildingId}`}
      id={buildingId}
      type="building-card"
      dirty={dirty}
      optionsContentArray={tabs}
      title={(
        <Link
          sx={{ fontSize: '5px' }}
          title={`Link to ${data?.name}`}
          to={getBuildingPath(id)}
        >
          {data?.name}
        </Link>
      )}
      titleAction={(
        <CardActionButtons
          closeDispatchFunc={close}
        />
      )}
    />
  );
}

export default BuildingCard;
